import { type AxiosError } from "axios";
import { defineMessages, useIntl } from "react-intl";

import {
  MutationActions,
  useEntityMutation,
  QueryKeys,
  queryClient,
} from "@/api/core";
import { useApi } from "@/hooks";
import { statusSchema, type Status } from "@/schemas";
import toast from "@/toast";
import { type ApiError } from "@/types/api";

import { updateStatusCache } from "../utils";

const messages = defineMessages({
  bookmarkSuccess: { id: "bookmark.created", defaultMessage: "Bookmarked!" },
  viewAll: { id: "bookmark.undo", defaultMessage: "View All" },
  deletedTruth: {
    id: "status.error.deletedTruth",
    defaultMessage: "This Truth has been deleted and is no longer available.",
  },
});

interface Variables {
  statusId: string;
}

function useBookmarkStatus() {
  const api = useApi();
  const intl = useIntl();

  return useEntityMutation({
    globalQueryKey: QueryKeys.statuses.bookmarks(),
    idAttributeFromVariables: "statusId",
    action: MutationActions.CREATE,
    schema: statusSchema,
    mutationFn: (variables: Variables) =>
      api.put<Status>(`/api/v2/bookmarks/statuses/${variables.statusId}`),
    onMutate(variables: Variables) {
      updateStatusCache(variables.statusId, () => ({
        bookmarked: true,
      }));
    },
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: QueryKeys.statuses.bookmarks(),
      });

      toast.success(intl.formatMessage(messages.bookmarkSuccess), {
        actionLabel: messages.viewAll,
        actionLink: "/bookmarks",
      });
    },
    onError(error: AxiosError<ApiError>, variables) {
      const data = error.response?.data;

      updateStatusCache(variables.statusId, () => ({
        bookmarked: false,
      }));
      if (error.response?.status === 404) {
        toast.error(intl.formatMessage(messages.deletedTruth));
      } else if (data?.error) {
        toast.error(data.error);
      }
    },
  });
}

export { useBookmarkStatus };
